import { useMemo } from 'react';
import {
    generatePath, Link, matchPath, useLocation, useParams,
} from 'react-router-dom';
import { faCalculator } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';

import { URLS } from 'services/urls';
import { TAB_TO_SLUG } from 'views/DealDashboard/constants';
import { useSlideout } from 'contexts/BackOfEnvelopeContext';
import { NSButton } from 'bricks';
import { FEATURE_IDS } from 'services/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { useAuthContext } from 'contexts/AuthContext';
import { isTurkUserClass } from 'constants/auth';
import { AccessLevels } from 'types/auth';
import UserMenu from './components/UserMenu/UserMenu';
import OrganizationMenu from './components/OrganizationMenu/OrganizationMenu';
import CannyChangelogWidget from './components/CannyChangelogWidget/CannyChangelogWidget';
import './NSNavbar.scss';
import PowerToolsMenu from './components/PowerToolsMenu/PowerToolsMenu';
import { config } from 'config';
import {NS_LOGO_URL, DEV_LOGO_URL} from 'constants/branding';

export const DisplayedLogo: { [key: string]: string } = {
    development: DEV_LOGO_URL,
    production: NS_LOGO_URL,
};

const MainNav = ({ pathname }: { pathname: string }) => {
    const { isFeatureAccessibleToUser, user } = useAuthContext();

    if (isTurkUserClass(user?.userClass)) {
        return null;
    }

    const hasContactsAccess = isFeatureAccessibleToUser([FEATURE_IDS.CRM_CONTACTS], AccessLevels.READ_ACCESS);
    const hasCompaniesAccess = isFeatureAccessibleToUser([FEATURE_IDS.CRM_COMPANIES], AccessLevels.READ_ACCESS);

    return (
        <div className="NSNavbar__link-list d-flex">
            <Link to="/deals" className={`NSNavbar__link-list__link d-flex align-items-center px-2 ${pathname === '/deals' ? 'active' : ''}`}>
                Deal Management
            </Link>
            <Link
                to="/deals/pro-formas"
                className={`NSNavbar__link-list__link d-flex align-items-center px-2 ${pathname === '/deals/pro-formas'
                    ? 'active'
                    : ''}`}
            >
                Pro Formas
            </Link>
            <Link to="/reports" className={`NSNavbar__link-list__link d-flex align-items-center px-2 ${pathname === '/reports' ? 'active' : ''}`}>
                Reports
            </Link>

            <Link
                to="/investment-memo"
                className={`NSNavbar__link-list__link d-flex align-items-center px-2 ${pathname === '/investment-memo' ? 'active' : ''}`}
            >
                Investment Memo
            </Link>

            <RenderIf isTrue={hasContactsAccess}>
                <a href={URLS.PROJECTS_APP.CONTACTS} className="NSNavbar__link-list__link d-flex align-items-center px-2">
                    Contacts
                </a>
            </RenderIf>
            <RenderIf isTrue={hasCompaniesAccess}>
                <a href={URLS.PROJECTS_APP.COMPANIES} className="NSNavbar__link-list__link d-flex align-items-center px-2">
                    Companies
                </a>
            </RenderIf>

            <PowerToolsMenu />
        </div>
    );
};

const DealDashboardNav = ({ pathname, dealId }: { pathname: string; dealId: string }) => {
    const dealDashboardPathMatch = matchPath(
        { path: URLS.DEAL_DASHBOARD.HOME, end: true },
        pathname,
    );

    const dealDashboardSubTabSlugs = Object.values(TAB_TO_SLUG);
    const isDealDashboardActive = dealDashboardPathMatch !== null
        && (!dealDashboardPathMatch.params.tab || dealDashboardSubTabSlugs.includes(dealDashboardPathMatch.params.tab));

    return (
        <div className="NSNavbar__link-list d-flex">
            <Link
                to={generatePath(URLS.DEAL_DASHBOARD.HOME, { dealId })}
                className={classNames('NSNavbar__link-list__link d-flex align-items-center px-2', {
                    active: isDealDashboardActive,
                })}
            >
                Deal Dashboard
            </Link>
            <Link
                to={generatePath(URLS.PRO_FORMA.HOME, { dealId })}
                className={classNames('NSNavbar__link-list__link d-flex align-items-center px-2', {
                    active: pathname === generatePath(URLS.PRO_FORMA.HOME, { dealId }),
                })}
            >
                Pro Forma
            </Link>
            <Link
                to={generatePath(URLS.DEAL_INVESTMENT_MEMO, { dealId })}
                className={classNames('NSNavbar__link-list__link d-flex align-items-center px-2', {
                    active: pathname === generatePath(URLS.DEAL_INVESTMENT_MEMO, { dealId }),
                })}
            >
                Investment Memo
            </Link>
            <Link
                to={generatePath(URLS.DOCUMENTS, { dealId })}
                className={classNames('NSNavbar__link-list__link d-flex align-items-center px-2', {
                    active: pathname === generatePath(URLS.DOCUMENTS, { dealId }),
                })}
            >
                Documents
            </Link>
            <Link
                to={generatePath(URLS.REPORT_BUILDER, { dealId })}
                className={classNames('NSNavbar__link-list__link d-flex align-items-center px-2', {
                    active: pathname === generatePath(URLS.REPORT_BUILDER, { dealId }),
                })}
            >
                Report Builder
            </Link>
        </div>
    );
};

const NSNavbar = () => {
    const { userOrganizations, isLoadingUserOrganizations, user } = useAuthContext();
    const location = useLocation();
    const { dealId } = useParams<{ dealId: string }>();
    const { toggleSlideout } = useSlideout();
    const { pathname } = location;
    const isDealPage = pathname.startsWith('/deal/') && Boolean(dealId);

    const navigation = useMemo(() => {
        if (isTurkUserClass(user?.userClass)) {
            return null;
        }
        if (isDealPage) {
            return <DealDashboardNav pathname={pathname} dealId={dealId!} />;
        }
        return <MainNav pathname={pathname} />;
    }, [pathname, dealId, isDealPage, user?.userClass]);

    const logoLinkDestination = URLS.DEALS.HOME;

    const organizations = userOrganizations.map(org => org.organization);

    const currentLogo = DisplayedLogo[config.environment] || NS_LOGO_URL;

    return (
        <nav className="NSNavbar d-flex justify-content-between px-3 ">
            <div className="d-flex align-items-center">
                <div className="mr-2">
                    <Link to={logoLinkDestination} data-testid="home_url">
                        <img src={currentLogo} style={{ height: '40px' }} alt="Logo" id="nav-bar-logo" />
                    </Link>
                </div>
                {navigation}
            </div>
            <div className="NSNavbar__right d-flex align-items-center">
                <div className="NSNavbar__right__icon-list d-flex align-items-center">
                    <RenderIf isTrue={organizations.length > 1 && !isLoadingUserOrganizations}>
                        <OrganizationMenu organizations={organizations} />
                    </RenderIf>
                    <NSButton
                        id="back-of-envelope-nav-button"
                        icon={faCalculator}
                        iconSize="lg"
                        color="secondary"
                        callback={toggleSlideout}
                        className="py-2 border-none NSButton--secondary-lighten text-nowrap"
                        featureId={FEATURE_IDS.BACK_OF_ENVELOPE}
                        ariaLabel="calculator"
                        allowReadOnlyAccess
                        tooltip="Assess deal financial feasibility with the Back-Of-Envelope calculator."
                    >
                        <span className="ml-2">Assess feasibility</span>
                    </NSButton>
                    <CannyChangelogWidget />
                    <UserMenu />
                </div>
            </div>
        </nav>
    );
};

export default NSNavbar;
